import { useId, useState, type FormEvent } from 'react';
import {
  Bouton, Carte, IconeCorbeille, IconePlus, Message, Squelette,
} from '@journal/ui';
import { useDonnees, useEcriture } from '../lib/donnees.ts';
import { useSession } from '../lib/session.tsx';

/**
 * Pense-bête.
 *
 * ── Ce qu'il remplace ───────────────────────────────────────────────────────
 * Le papier collé au bord de l'écran. « Photocopier l'évaluation »,
 * « rapporter les autorisations », « appeler les parents de Léa ». Rien de
 * cela n'est une séance : ni le cahier journal ni les progressions n'en
 * veulent, et les y ranger polluerait deux documents qu'on montre.
 *
 * ── Sa place, juste sous le récapitulatif du jour ───────────────────────────
 * C'est là qu'on regarde en arrivant le matin et avant de partir le soir. Une
 * liste rangée dans un module qu'il faut ouvrir n'est plus une liste de
 * choses à faire : c'est une liste qu'on oublie.
 *
 * En dessous et non à côté : le récapitulatif porte des intitulés de séance
 * entiers, et lui prendre un tiers de sa largeur les faisait passer à la ligne
 * tous les trois mots.
 *
 * ── La coche est à DROITE ───────────────────────────────────────────────────
 * En bout de ligne, là où le regard arrive après avoir lu le texte, et là où
 * le pouce arrive sur tablette. À gauche, elle sépare le texte de la marge et
 * force à viser avant de lire.
 */

interface Tache {
  id: string;
  label: string;
  done: boolean;
  position: number;
}

export function PenseBete() {
  const { api } = useSession();
  const taches = useDonnees<Tache[]>('/todos');
  const { executer, enCours, erreur } = useEcriture();
  const [saisie, setSaisie] = useState('');

  const liste = taches.donnees ?? [];
  const faites = liste.filter((t) => t.done).length;

  async function ajouter(e: FormEvent) {
    e.preventDefault();
    const label = saisie.trim();
    if (label === '') return;

    /*
     * Le champ se vide AVANT la réponse.
     *
     * On tape trois tâches à la suite : attendre l'aller-retour entre chacune
     * fait retaper par-dessus le texte précédent quand la réponse arrive en
     * retard. En cas d'échec, le message le dit et le texte est encore dans le
     * presse-papier de la tête — mieux que la frappe suivante perdue.
     */
    setSaisie('');
    if (await executer(() => api.requete('/todos', { methode: 'POST', corps: { label } }))) {
      await taches.recharger();
    }
  }

  /**
   * Cocher, tout de suite à l'écran.
   *
   * Une case qui attend l'aller-retour du réseau se coche « en retard » et
   * donne l'impression d'un clic manqué — on reclique, et on la décoche. On
   * bascule donc l'affichage d'abord, et l'on recharge après pour retomber sur
   * la vérité du serveur si l'écriture a échoué.
   */
  async function basculer(tache: Tache) {
    taches.definir(liste.map((t) => (
      t.id === tache.id ? { ...t, done: !t.done } : t
    )));
    await executer(() => api.requete(`/todos/${tache.id}`, {
      methode: 'PATCH', corps: { done: !tache.done },
    }));
    await taches.recharger();
  }

  async function supprimer(tache: Tache) {
    // Sans confirmation : une ligne de pense-bête se retape en trois secondes,
    // et demander « êtes-vous sûr ? » pour cela use la confirmation là où elle
    // compte vraiment — un élève, une séance.
    if (await executer(() => api.requete(`/todos/${tache.id}`, { methode: 'DELETE' }))) {
      await taches.recharger();
    }
  }

  async function viderFaites() {
    if (await executer(() => api.requete('/todos', { methode: 'DELETE' }))) {
      await taches.recharger();
    }
  }

  return (
    <Carte
      titre="À faire"
      action={faites > 0 ? (
        <Bouton
          variante="secondary"
          taille="sm"
          onClick={() => void viderFaites()}
          disabled={enCours}
        >
          {`Vider les ${faites} faites`}
        </Bouton>
      ) : undefined}
    >
      <form onSubmit={ajouter} className="flex gap-2">
        <input
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          maxLength={200}
          // Sans texte d'exemple : le champ est bordé du titre « À faire » et
          // du bouton « Ajouter », qui disent déjà ce qu'on écrit dedans. Un
          // exemple s'y lisait comme une tâche déjà saisie.
          aria-label="Nouvelle tâche"
          className={[
            'min-w-0 flex-1 rounded-lg border px-3 py-2 text-sm',
            'border-[var(--color-border-strong)] bg-[var(--color-surface-input)]',
            'text-[var(--color-fg)] placeholder:text-[var(--color-fg-subtle)]',
            'focus-visible:outline-2 focus-visible:outline-offset-2',
            'focus-visible:outline-[var(--color-focus)]',
          ].join(' ')}
        />
        <Bouton
          type="submit"
          variante="secondary"
          icone={<IconePlus taille={16} />}
          disabled={enCours || saisie.trim() === ''}
        >
          Ajouter
        </Bouton>
      </form>

      {erreur && <div className="mt-3"><Message ton="erreur">{erreur}</Message></div>}

      {taches.chargement && !taches.donnees ? (
        <div className="mt-3 flex flex-col gap-1.5">
          <Squelette className="h-9" />
          <Squelette className="h-9" />
        </div>
      ) : liste.length === 0 ? (
        /*
         * Rien du tout quand la liste est vide : le champ de saisie dit déjà à
         * quoi sert la carte, et une phrase pour dire « rien » n'apportait
         * rien de plus.
         */
        null
      ) : (
        <ul className="mt-3 flex flex-col gap-1">
          {liste.map((t) => (
            <LigneTache
              key={t.id}
              tache={t}
              occupe={enCours}
              surBascule={() => void basculer(t)}
              surSuppression={() => void supprimer(t)}
            />
          ))}
        </ul>
      )}
    </Carte>
  );
}

/**
 * Une ligne de la liste.
 *
 * Extraite parce qu'elle a besoin d'un identifiant propre : la case est en
 * BOUT de ligne, donc après le texte dans le DOM, et c'est `htmlFor` qui les
 * relie. Sans cette liaison, cliquer sur le texte ne cocherait pas, et un
 * lecteur d'écran annoncerait une case sans nom.
 */
function LigneTache({
  tache, occupe, surBascule, surSuppression,
}: {
  tache: Tache;
  occupe: boolean;
  surBascule: () => void;
  surSuppression: () => void;
}) {
  const id = useId();
  return (
    <li
      className={[
        'group flex items-start gap-2 rounded-lg border px-3 py-2',
        'border-[var(--color-border)] bg-[var(--color-surface)]',
      ].join(' ')}
    >
      {/*
        * ── Une tâche longue PASSE À LA LIGNE ─────────────────────────────
        * `min-w-0` d'abord : un enfant de boîte flexible refuse par défaut de
        * descendre sous la largeur de son contenu, et la ligne débordait de la
        * carte au lieu de se replier.
        *
        * `overflow-wrap: anywhere` ensuite, plutôt que la coupure aux espaces
        * seule : une adresse ou une référence collée — « photocopier
        * DOC-2026-EVAL-MATHS-CE2 » — est un seul mot, que rien n'autorise à
        * couper sans cela.
        *
        * `items-start` sur la ligne pour que la coche reste en tête quand le
        * texte occupe deux lignes ; centrée, elle flotterait au milieu.
        */}
      <label
        htmlFor={id}
        className={[
          'min-w-0 flex-1 cursor-pointer text-sm leading-snug [overflow-wrap:anywhere]',
          tache.done
            ? 'text-[var(--color-fg-subtle)] line-through'
            : 'text-[var(--color-fg)]',
        ].join(' ')}
      >
        {tache.label}
      </label>

      {/*
        * Supprimer : discret tant qu'on ne survole pas la ligne. Une corbeille
        * par ligne à pleine intensité ferait une colonne de corbeilles plus
        * visible que les tâches elles-mêmes. Le focus la révèle aussi — sans
        * quoi elle serait inatteignable au clavier.
        */}
      <button
        type="button"
        onClick={surSuppression}
        disabled={occupe}
        aria-label={`Supprimer « ${tache.label} »`}
        className={[
          'mt-px shrink-0 rounded-md p-1 text-[var(--color-fg-subtle)]',
          'opacity-0 transition-opacity duration-150',
          'hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-danger)]',
          'group-hover:opacity-100 focus-visible:opacity-100',
        ].join(' ')}
      >
        <IconeCorbeille taille={14} />
      </button>

      {/*
        * La coche, EN BOUT DE LIGNE.
        *
        * C'est la vraie case, pas un décor : elle se tabule, se coche à
        * l'espace, porte son anneau de focus, et reçoit la marge de visée
        * tactile posée par la feuille de styles. Un faux carré avec un
        * `onClick` n'aurait eu aucune des quatre.
        */}
      <input
        id={id}
        type="checkbox"
        checked={tache.done}
        onChange={surBascule}
        className={[
          'mt-0.5 size-[1.15rem] shrink-0 cursor-pointer rounded',
          'border border-[var(--color-border-strong)] bg-[var(--color-surface-input)]',
          'accent-[var(--color-primary)]',
        ].join(' ')}
      />
    </li>
  );
}
